import React, { useState } from "react";
import { Link } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { InvoiceStatusStepper } from "@app.automotus.io/components/InvoiceStatusStepper";
import { InvoiceVehicleDetails } from "@app.automotus.io/components/InvoiceVehicleDetails";
import { InvoiceParkEvents } from "@app.automotus.io/components/InvoiceParkEvents";
import { InvoiceGrandTotal } from "@app.automotus.io/components/InvoiceGrandTotal";
import { DownloadInvoicePDFButton } from "@app.automotus.io/components/DownloadInvoicePDFButton";
import { LoadingSkeleton } from "@app.automotus.io/components/LoadingSkeleton";
import { VoidNoticeDialog } from "@curbsuite/components/VoidNoticeDialog";
import { RecordNoticePaymentDialog } from "@curbsuite/components/RecordNoticePaymentDialog";
import { PendingVoidInvoiceDialog } from "@curbsuite/components/PendingVoidInvoiceDialog";
import { InvoiceData, InvoiceReviewStatus } from "common/graphql";
import { useFeatureFlags } from "@app.automotus.io/components/hooks";
import { FEATURE_FLAGS } from "@curbsuite/constants";

export interface InvoicePageContentProps {
  invoiceId: string;
  invoice: InvoiceData | null;
  loading: boolean;
  backButtonLink: string;
  showVoidButton: boolean;
  showRecordPaymentButton: boolean;
  onVoidSubmit: (values: { reason: string; voidedAt: Date }) => void | Promise<void>;
  onRecordPaymentSubmit: (values: { paidAmountCents: number; paidAt: Date }) => void | Promise<void>;
  onIssueSubmit: () => void | Promise<void>;
  onPendingVoidSubmit: () => void | Promise<void>;
  voidSubmitting: boolean;
  recordPaymentSubmitting: boolean;
  issueSubmitting: boolean;
  pendingVoidSubmitting: boolean;
  onNavigateToTransaction: (transactionId: string) => void;
}

export const InvoicePageContent: React.FC<InvoicePageContentProps> = ({
  invoiceId,
  invoice,
  loading,
  backButtonLink,
  showVoidButton,
  showRecordPaymentButton,
  onVoidSubmit,
  onRecordPaymentSubmit,
  onIssueSubmit,
  onPendingVoidSubmit,
  voidSubmitting,
  recordPaymentSubmitting,
  issueSubmitting,
  pendingVoidSubmitting,
  onNavigateToTransaction,
}) => {
  const { isEnabled } = useFeatureFlags();
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = useState(false);
  const [pendingVoidDialogOpen, setPendingVoidDialogOpen] = useState(false);

  const showPendingActions =
    isEnabled(FEATURE_FLAGS.INVOICE_REVIEW_IS_ENABLED) && invoice?.review_status === InvoiceReviewStatus.PENDING;

  return (
    <>
      <Stack spacing={3} width={"100%"}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", columnGap: 1 }}>
              <IconButton component={Link} to={backButtonLink} sx={{ gridRow: "1 / 3" }}>
                <ArrowBackIosIcon />
              </IconButton>
              <Box sx={{ gridColumn: 2 }}>
                <LoadingSkeleton loading={loading}>
                  <Typography variant={"h4"} fontWeight={600}>
                    Invoice Number: {`${invoice?.index}`.padStart(6, "0")}
                  </Typography>
                </LoadingSkeleton>
              </Box>
              <Box sx={{ gridColumn: 2 }}>
                <LoadingSkeleton loading={loading}>
                  <Typography variant={"subtitle1"} fontSize={12}>
                    A detailed description of an invoice
                  </Typography>
                </LoadingSkeleton>
              </Box>
            </Box>
            {showPendingActions ? (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="error"
                  disabled={pendingVoidSubmitting || issueSubmitting}
                  sx={{ textTransform: "none" }}
                  onClick={() => setPendingVoidDialogOpen(true)}
                >
                  Void
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={issueSubmitting || pendingVoidSubmitting}
                  sx={{ textTransform: "none" }}
                  onClick={onIssueSubmit}
                >
                  Issue
                </Button>
              </Stack>
            ) : showVoidButton || showRecordPaymentButton ? (
              <Stack direction="row" spacing={2}>
                {showVoidButton && (
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ textTransform: "none" }}
                    onClick={() => setVoidDialogOpen(true)}
                  >
                    Void
                  </Button>
                )}
                {showRecordPaymentButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "none" }}
                    onClick={() => setRecordPaymentDialogOpen(true)}
                  >
                    Record Payment
                  </Button>
                )}
              </Stack>
            ) : null}
          </Box>
        </Box>
        <InvoiceStatusStepper invoiceId={invoiceId} />
        <InvoiceVehicleDetails invoiceId={invoiceId} />
        <InvoiceParkEvents invoiceId={invoiceId} onClickParkEvent={onNavigateToTransaction} />
        <InvoiceGrandTotal invoiceId={invoiceId} />
        {invoice?.voided_at && invoice?.replaced_by_invoice ? (
          <Button
            variant={"contained"}
            sx={{ textTransform: "none" }}
            fullWidth
            component={Link}
            to={`/invoices/${invoice.replaced_by_invoice.id}`}
          >
            View Revised Invoice
          </Button>
        ) : (
          <DownloadInvoicePDFButton invoiceId={invoiceId} />
        )}
      </Stack>
      <VoidNoticeDialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        onSubmit={async (values) => {
          await onVoidSubmit(values);
          setVoidDialogOpen(false);
        }}
        entityLabel="Invoice"
        isSubmitting={voidSubmitting}
      />
      <RecordNoticePaymentDialog
        open={recordPaymentDialogOpen}
        onClose={() => setRecordPaymentDialogOpen(false)}
        onSubmit={async (values) => {
          await onRecordPaymentSubmit(values);
          setRecordPaymentDialogOpen(false);
        }}
        entityLabel="Invoice"
        isSubmitting={recordPaymentSubmitting}
      />
      <PendingVoidInvoiceDialog
        open={pendingVoidDialogOpen}
        onClose={() => setPendingVoidDialogOpen(false)}
        onConfirm={async () => {
          await onPendingVoidSubmit();
          setPendingVoidDialogOpen(false);
        }}
        isSubmitting={pendingVoidSubmitting}
      />
    </>
  );
};

export default InvoicePageContent;
